import { type TableColumn, createTextColumn } from '@ipfabric/utils';
import { Button } from 'components/Button/Button';

import { Link } from '@ipfabric/ui-components/Link/Link';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type { SimpleConfirmDialogHook } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { useMemo } from 'react';
import { useApiScopeRequirementMet } from 'utils/permissions/useApiScopeRequirementMet';
import type { Extension, ExtensionsModel } from '../model/ExtensionsModel';
import { showExtensionDetailsDialog } from '../model/commands/showExtensionDetailsDialog';
import { startExtension as startExtensionCommand } from '../model/commands/startExtension';
import { stopExtension as stopExtensionCommand } from '../model/commands/stopExtension';
import { unregisterExtension as unregisterExtensionCommand } from '../model/commands/unregisterExtension';

type ExtensionTableRow = {
  id: string;
  name: string;
  description: string | null;
  url: string;
  status: string;
  resources: {
    cpu: number;
    memory: number;
  };
};

const columnsMsg = {
  name: 'Name',
  description: 'Description',
  status: 'Status',
  url: 'URL',
};

export const useExtensionsTableColumns = (props: {
  model: ExtensionsModel;
  interactionsConfirmDialog: SimpleConfirmDialogHook;
  removalConfirmDialog: SimpleConfirmDialogHook;
}): {
  columns: TableColumn<ExtensionTableRow>[];
  data: Extension[];
} => {
  const apiScopeRequirementMet = useApiScopeRequirementMet();
  const dispatch = props.model.dispatch;
  const isAllowedToSeeLogs = apiScopeRequirementMet({
    method: 'GET',
    path: '/extensions/:extensionId/logs',
  });
  const interactionsConfirmDialog = props.interactionsConfirmDialog;
  const removalConfirmDialog = props.removalConfirmDialog;

  const openExtensionDetailsDialog = useStableCallback(
    (canSeeLogs: boolean, extensionId: string) => {
      dispatch(showExtensionDetailsDialog, { canSeeLogs, extensionId });
    },
  );

  const startExtension = useStableCallback(async (extensionId: string) => {
    if (
      await interactionsConfirmDialog.show(
        {
          btnConfirm: 'Yes',
          btnSecondaryConfirm: 'No',
          header: 'Start extension',
          subHeader: 'Are you sure you want to start this extension?',
        },
        {
          children: (
            <p>
              After starting the extension, it will become accessible via the
              extension URL.
            </p>
          ),
        },
      )
    ) {
      dispatch(startExtensionCommand, { extensionId });
    }
  });

  const stopExtension = useStableCallback(async (extensionId: string) => {
    if (
      await interactionsConfirmDialog.show(
        {
          btnConfirm: 'Yes',
          btnSecondaryConfirm: 'No',
          header: 'Stop extension',
          subHeader: 'Are you sure you want to stop this extension?',
        },
        {
          children: (
            <p>
              After stopping the extension, it will no longer be accessible via
              the extension URL and any running operations will be stopped.
            </p>
          ),
        },
      )
    ) {
      dispatch(stopExtensionCommand, { extensionId });
    }
  });

  const unregisterExtension = useStableCallback(async (extensionId: string) => {
    if (
      await removalConfirmDialog.show(
        {
          btnConfirm: 'Yes',
          btnSecondaryConfirm: 'No',
          header: 'Unregister extension',
          subHeader: 'Are you sure you want to unregister this extension?',
        },
        {
          children: (
            <p>
              After unregistering the extension, it will no longer be accessible
              via the extension URL and its data will be deleted.
            </p>
          ),
        },
      )
    ) {
      dispatch(unregisterExtensionCommand, { extensionId });
    }
  });

  const columns: TableColumn<ExtensionTableRow>[] = useMemo(
    () => [
      createTextColumn({
        key: 'name',
        title: columnsMsg.name,
      }),
      createTextColumn({
        key: 'description',
        title: columnsMsg.description,
      }),
      createTextColumn({
        key: 'status',
        title: columnsMsg.status,
      }),
      createFrontendOnlyColumn({
        key: 'url',
        title: columnsMsg.url,
        render: (row) => {
          return (
            <Link
              renderAs={'anchor'}
              href={row.url}
              elementProps={{ target: '_blank' }}
            >
              {row.url}
            </Link>
          );
        },
      }),
      createFrontendOnlyColumn({
        key: 'resources',
        title: 'Resource Limits',
        render: (row) => {
          return `CPU: ${row.resources?.cpu ?? 'not-set'}, Memory: ${row.resources?.memory ?? 'not-set'} MB`;
        },
      }),
      createFrontendOnlyColumn({
        key: 'actions',
        title: 'Actions',
        disabledSort: true,
        width: 140,
        fixedWidth: true,
        render: (row) => (
          <>
            {apiScopeRequirementMet({
              method: 'GET',
              path: '/extensions/:extensionId',
            }) && (
              <Button
                variant='tertiary'
                icon='fa-eye'
                type='button'
                size='xxs'
                onClick={() =>
                  openExtensionDetailsDialog(isAllowedToSeeLogs, row.id)
                }
              />
            )}
            {apiScopeRequirementMet({
              method: 'POST',
              path: '/extensions/:extensionId/start',
            }) && (
              <Button
                variant='tertiary'
                icon='fa-play'
                type='button'
                size='xxs'
                onClick={() => startExtension(row.id)}
              />
            )}
            {apiScopeRequirementMet({
              method: 'POST',
              path: '/extensions/:extensionId/stop',
            }) && (
              <Button
                variant='tertiary'
                icon='fa-stop'
                type='button'
                size='xxs'
                onClick={() => stopExtension(row.id)}
              />
            )}
            {apiScopeRequirementMet({
              method: 'DELETE',
              path: '/extensions/:extensionId',
            }) && (
              <Button
                variant='tertiary'
                icon='fa-trash-can'
                type='button'
                size='xxs'
                onClick={() => unregisterExtension(row.id)}
              />
            )}
          </>
        ),
      }),
    ],
    [apiScopeRequirementMet, isAllowedToSeeLogs],
  );

  return {
    columns,
    data: props.model.extensions.map((extension) => ({
      ...extension,
      url: `/extensions-apps/${extension.slug}`,
    })),
  };
};
